import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AlertTriangle, ArrowRightLeft, Eye, GitBranch, Info, Layers, Link2, OctagonX, PlugZap, RotateCcw, ShieldX, Shuffle, SquareDashed, Target, Unplug, } from 'lucide-react';
import { useState } from 'react';
import { formatClock, formatDuration, formatTokens } from '../lib/format.js';
import { Markdown } from '../lib/markdown.js';
import { parseCommand } from '../lib/summarize.js';
import { displayPath } from './ActivityRow.js';
import { cx, useViewer } from './primitives.js';
/** Git operation kinds (Claude `vcs_state_changed.kind`) as a past-tense verb. */
const VCS_VERB = {
    rebase: 'Rebased',
    commit: 'Committed on',
    merge: 'Merged into',
    pull: 'Pulled',
    push: 'Pushed',
    reset: 'Reset',
    checkout: 'Switched to',
    switch: 'Switched to',
    branch: 'Switched to',
    stash: 'Stashed on',
    cherry_pick: 'Cherry-picked onto',
};
function vcsLabel(m) {
    const verb = m.text ? VCS_VERB[m.text.toLowerCase().replace(/[-\s]/g, '_')] : undefined;
    if (verb)
        return m.to ? `${verb} ${m.to}` : verb.replace(/ (on|onto|into|to)$/, '');
    return m.text ?? (m.to ? `Branch: ${m.to}` : 'Repository changed');
}
const MARKER = {
    resumed: { icon: RotateCcw, label: (m) => m.text ?? 'Session resumed' },
    reconnected: { icon: PlugZap, label: (m) => m.text ?? 'Reconnected', tone: 'ok' },
    model_change: {
        icon: Shuffle,
        label: (m) => m.from && m.to ? `Model changed ${m.from} → ${m.to}` : (m.text ?? 'Model changed'),
    },
    mode_change: {
        icon: ArrowRightLeft,
        label: (m) => (m.to ? `Mode: ${m.to}` : (m.text ?? 'Mode changed')),
    },
    continued_from: {
        icon: Link2,
        label: (m) => m.text ?? `Continued from ${m.ref ?? 'a previous session'}`,
    },
    interrupted: { icon: OctagonX, label: (m) => m.text ?? 'Interrupted by you', tone: 'wait' },
    conversation_reset: { icon: SquareDashed, label: (m) => m.text ?? 'Conversation cleared' },
    review_mode: { icon: Eye, label: (m) => m.text ?? 'Review mode' },
    vcs_change: { icon: GitBranch, label: vcsLabel },
    goal: { icon: Target, label: (m) => m.text ?? (m.to ? `Goal: ${m.to}` : 'Goal updated') },
    // Not rendered as a row (the reducer lifts it into the session title); kept for completeness.
    title: { icon: Target, label: (m) => (m.text ? `Session named: ${m.text}` : 'Session named') },
};
/**
 * Divider-style event marker: compaction (with summary), resume, reconnect, model change …
 * Dividers are quiet (hairline + small label); only compaction is interactive.
 */
export function Marker({ item, related }) {
    if (item.type === 'compaction')
        return _jsx(CompactionMarker, { item: item });
    if (item.type === 'error')
        return _jsx(ErrorBanner, { item: item });
    if (item.type === 'notice')
        return _jsx(NoticeRow, { item: item, related: related });
    if (item.type === 'raw')
        return _jsx(RawRow, { item: item });
    const m = MARKER[item.marker];
    const Icon = m.icon;
    return (_jsxs("div", { className: cx('vk-marker', m.tone && `vk-marker--${m.tone}`), role: "separator", "aria-label": m.label(item), children: [_jsx("span", { className: "vk-marker__line", "aria-hidden": "true" }), _jsxs("span", { className: "vk-marker__label", children: [_jsx(Icon, { size: 13, "aria-hidden": "true" }), _jsx("span", { className: "vk-marker__text", children: m.label(item) }), _jsx("time", { className: "vk-marker__time", dateTime: new Date(item.ts).toISOString(), children: formatClock(item.ts) })] }), _jsx("span", { className: "vk-marker__line", "aria-hidden": "true" })] }));
}
export function CompactionMarker({ item, defaultOpen = false, }) {
    const [open, setOpen] = useState(defaultOpen);
    const tokens = item.preTokens != null && item.postTokens != null
        ? `${formatTokens(item.preTokens)} → ${formatTokens(item.postTokens)} tokens`
        : '';
    return (_jsxs("div", { className: "vk-compaction", children: [_jsxs("div", { className: "vk-marker", children: [_jsx("span", { className: "vk-marker__line", "aria-hidden": "true" }), _jsxs("button", { type: "button", className: "vk-marker__label vk-marker__btn", "aria-expanded": open, onClick: () => setOpen((o) => !o), children: [_jsx(Layers, { size: 13, "aria-hidden": "true" }), item.trigger === 'auto' ? 'Context auto-compacted' : 'Context compacted', tokens ? _jsx("span", { className: "vk-marker__time", children: tokens }) : null, item.summary ? (_jsx("span", { className: "vk-marker__more", children: open ? 'Hide summary' : 'Summary' })) : null] }), _jsx("span", { className: "vk-marker__line", "aria-hidden": "true" })] }), open && item.summary ? (_jsxs("div", { className: "vk-compaction__summary", children: [_jsxs("div", { className: "vk-meta", children: ["What the agent kept", item.durationMs ? ` · compacted in ${formatDuration(item.durationMs)}` : ''] }), _jsx(Markdown, { text: item.summary })] })) : null] }));
}
export function ErrorBanner({ item, }) {
    return (_jsxs("div", { className: cx('vk-banner', item.retrying ? 'vk-banner--wait' : 'vk-banner--fail'), role: "alert", children: [item.retrying ? (_jsx(RotateCcw, { size: 15, "aria-hidden": "true" })) : (_jsx(OctagonX, { size: 15, "aria-hidden": "true" })), _jsxs("div", { className: "vk-banner__body", children: [_jsx("strong", { children: item.retrying ? 'Retrying' : item.fatal ? 'Run stopped' : 'Error' }), _jsx("span", { children: item.message }), item.code ? _jsx("code", { className: "vk-mono vk-meta", children: item.code }) : null] })] }));
}
const FILE_VERB = { edit: 'Edit', read: 'Read', write: 'Write' };
export function parseDenial(text) {
    const t = text.trim();
    const out = {};
    const reason = /\bReason:\s*([\s\S]+?)\s*$/.exec(t);
    if (reason?.[1])
        out.reason = reason[1];
    const use = /^Permission to use (\S+?)(?: with (command )?([\s\S]+?))? has been denied/.exec(t);
    if (use) {
        out.tool = use[1];
        if (use[3]) {
            if (use[2])
                out.command = use[3];
            else
                out.target = use[3];
        }
        return out;
    }
    const file = /^Permission to (edit|read|write) ([\s\S]+?) has been denied/.exec(t);
    if (file?.[1] && file[2])
        return { ...out, tool: FILE_VERB[file[1]], target: file[2] };
    const short = /^(\S+) denied$/.exec(t);
    if (short?.[1] && short[1] !== 'tool')
        out.tool = short[1];
    return out;
}
/** Long plain notices clamp to a few lines; the full text is one click away. */
const CLAMP_CHARS = 280;
const CLAMP_LINES = 4;
export function NoticeRow({ item, related }) {
    if (item.code === 'permission_denied')
        return _jsx(DeniedNotice, { item: item, related: related });
    return _jsx(PlainNotice, { item: item });
}
function PlainNotice({ item }) {
    const [open, setOpen] = useState(false);
    const Icon = item.level === 'warning' ? AlertTriangle : Info;
    const long = item.text.length > CLAMP_CHARS || item.text.split('\n').length > CLAMP_LINES;
    return (_jsxs("div", { className: cx('vk-notice', `vk-notice--${item.level}`), children: [_jsx(Icon, { size: 14, "aria-hidden": "true", className: "vk-notice__icon" }), _jsxs("div", { className: "vk-notice__body", children: [_jsx("p", { className: cx('vk-notice__text', long && !open && 'is-clamped'), children: item.text }), long ? (_jsx("button", { type: "button", className: "vk-notice__more", "aria-expanded": open, onClick: () => setOpen(!open), children: open ? 'Show less' : 'Show all' })) : null] })] }));
}
function DeniedNotice({ item, related }) {
    const { cwd } = useViewer();
    const [open, setOpen] = useState(false);
    const d = parseDenial(item.text);
    const tool = d.tool ?? related?.name;
    const command = d.command ?? (d.target ? undefined : related?.command);
    const target = d.target ?? (command ? undefined : related?.path);
    const parsed = command ? parseCommand(command, cwd) : undefined;
    return (_jsxs("div", { className: "vk-notice vk-notice--denied", role: "note", "aria-label": `Permission denied${tool ? `: ${tool}` : ''}`, children: [_jsx(ShieldX, { size: 15, "aria-hidden": "true", className: "vk-notice__icon" }), _jsxs("div", { className: "vk-notice__body", children: [_jsxs("div", { className: "vk-notice__head", children: [_jsx("strong", { children: "Permission denied" }), tool ? _jsx("span", { className: "vk-notice__tool", children: tool }) : null] }), parsed ? (_jsxs("div", { className: "vk-notice__line", children: [parsed.dir ? _jsx("span", { className: "vk-notice__dir vk-mono", children: parsed.dir }) : null, _jsx("code", { className: "vk-notice__cmd vk-mono", children: parsed.title })] })) : target ? (_jsx("div", { className: "vk-notice__line", children: _jsx("code", { className: "vk-notice__cmd vk-mono", children: displayPath(target, cwd) }) })) : null, d.reason ? _jsx("p", { className: "vk-notice__reason", children: d.reason }) : null, !parsed && !target && !d.reason ? _jsx("p", { className: "vk-notice__reason", children: item.text }) : null, parsed || target || d.reason ? (_jsx("button", { type: "button", className: "vk-notice__more", "aria-expanded": open, onClick: () => setOpen(!open), children: open ? 'Hide details' : parsed ? 'Show full command' : 'Show full message' })) : null, open ? (_jsxs("div", { className: "vk-notice__details", children: [parsed ? _jsx("pre", { className: "vk-notice__full vk-mono", children: parsed.full }) : null, _jsx("p", { className: "vk-notice__msg", children: item.text })] })) : null] })] }));
}
export function RawRow({ item }) {
    return (_jsxs("details", { className: "vk-raw", children: [_jsxs("summary", { children: [_jsx(Unplug, { size: 13, "aria-hidden": "true" }), " Unrecognised event", ' ', _jsx("code", { className: "vk-mono", children: item.source })] }), _jsx("pre", { className: "vk-raw__pre", children: JSON.stringify(item.data, null, 2) })] }));
}
//# sourceMappingURL=Marker.js.map